import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useScrollSnap } from '../hooks/useScrollSnap';
import { useAdmin } from '../context/AdminContext';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import FadeInOnScroll from '../components/shared/FadeInOnScroll';
import FullPageSection from '../components/shared/FullPageSection';
import Footer from '../components/shared/Footer';
import { newsAPI } from '../services/api';
import '../App.css';

import Alys_80b from '../assets/images/sides/Alys_80b.png';
import Bianca97 from '../assets/images/sides/Bianca97.png';
import Bianca111b from '../assets/images/sides/Bianca111b.png';
import Kris_36c from '../assets/images/sides/Kris_36c.png';
import Kris_33a from '../assets/images/sides/Kris_33a.png';
import Rico_1b from '../assets/images/sides/Rico_1b.png';

const defaultSectionImages = {
  about: {
    left: Alys_80b,
    right: Bianca97
  },
  studios: {
    left: Bianca111b,
    right: Kris_36c
  },
  vague: {
    left: Kris_33a,
    right: Rico_1b
  },
  careers: {
    left: Alys_80b,
    right: Bianca111b
  },
  partners: {
    left: Bianca111b,
    right: Kris_36c
  },
  news: {
    left: Kris_33a,
    right: Rico_1b
  },

  // Legacy fallback for old saved admin data.
  opportunities: {
    left: Alys_80b,
    right: Bianca111b
  }
};

const VelouraLanding = ({ selectedBrand }) => {
  const { sectionImages: adminSectionImages, isInitialized: adminInitialized } = useAdmin();

  const savedVelouraImages = adminInitialized && adminSectionImages && adminSectionImages.veloura
    ? adminSectionImages.veloura
    : {};

  const sectionImages = {
    ...defaultSectionImages,
    ...savedVelouraImages,

    about: {
      ...defaultSectionImages.about,
      ...(savedVelouraImages.about || {})
    },

    studios: {
      ...defaultSectionImages.studios,
      ...(savedVelouraImages.studios || {})
    },

    vague: {
      ...defaultSectionImages.vague,
      ...(savedVelouraImages.vague || {})
    },

    careers: {
      ...defaultSectionImages.careers,
      ...(savedVelouraImages.opportunities || {}),
      ...(savedVelouraImages.careers || {})
    },

    partners: {
      ...defaultSectionImages.partners,
      ...(savedVelouraImages.opportunities || {}),
      ...(savedVelouraImages.partners || {})
    },

    news: {
      ...defaultSectionImages.news,
      ...(savedVelouraImages.opportunities || {}),
      ...(savedVelouraImages.news || {})
    },

    opportunities: {
      ...defaultSectionImages.opportunities,
      ...(savedVelouraImages.opportunities || {})
    }
  };

  const [scrolled, setScrolled] = useState(false);
  const [landingNews, setLandingNews] = useState([]);
  const [landingNewsLoading, setLandingNewsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadLandingNews = async () => {
      try {
        const response = await newsAPI.getLanding();
        if (active && response.success) {
          setLandingNews((response.data || []).slice(0, 4));
        }
      } catch (error) {
        console.error('Error loading landing page news:', error);
      } finally {
        if (active) {
          setLandingNewsLoading(false);
        }
      }
    };

    loadLandingNews();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useScrollSnap(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.3,
        ease: 'easeOut',
        delay: 0.2
      }}
      className="App"
    >
      <ResponsiveNavbar
        menuItems={[
          { type: 'link', label: 'Home', to: '#home' },
          { type: 'link', label: 'About', to: '#about' },
          { type: 'link', label: 'Studios', to: '/studios' },
          { type: 'link', label: 'Team', to: '/team' },
          { type: 'link', label: 'Vague', to: '#vague' },
          { type: 'link', label: 'Careers', to: '/careers' },
          { type: 'link', label: 'Partners', to: '/partners' },
          { type: 'link', label: 'News', to: '/news' },
          { type: 'link', label: 'Gallery', to: '/studios#gallery' },
          { type: 'link', label: 'Shop', to: '/shop' },
          { type: 'link', label: 'Account', to: '/account' }
        ]}
        logo="VELOURA"
        logoLink={null}
        scrolled={scrolled}
      />

      <section id="news" className="landing-news-section" data-scroll-section>
        <div className="landing-news-container">
          <FadeInOnScroll>
            <div className="landing-news-heading">
              <span className="landing-news-kicker">Latest From Veloura</span>
              <h2 className="section-title">News</h2>
              <p>Releases, announcements, events, and projects from across Veloura.</p>
            </div>
          </FadeInOnScroll>

          {landingNewsLoading ? (
            <div className="landing-news-status">Loading latest news...</div>
          ) : landingNews.length > 0 ? (
            <div className="landing-news-grid">
              {landingNews.map((article, index) => {
                const isInternal = /^\/(?!\/)/.test(article.full_article_url || '');
                return (
                  <FadeInOnScroll key={article.id} delay={0.1 + (index * 0.08)}>
                    <article className="landing-news-card">
                      <div className="landing-news-image-wrap">
                        {article.background_image_url ? (
                          <img
                            src={article.background_image_url}
                            alt=""
                            className="landing-news-image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="landing-news-image-placeholder" aria-hidden="true">
                            Veloura
                          </div>
                        )}
                      </div>

                      <div className="landing-news-card-body">
                        <div className="landing-news-meta">
                          <span>{article.category || 'Company News'}</span>
                          <time dateTime={article.submitted_at}>
                            {new Date(article.submitted_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </time>
                        </div>
                        <h3>{article.title}</h3>
                        <p>{article.description || 'Read the latest update from Veloura.'}</p>
                        <a
                          href={article.full_article_url}
                          className="landing-news-read-more"
                          {...(!isInternal && { target: '_blank', rel: 'noopener noreferrer' })}
                        >
                          Read More
                        </a>
                      </div>
                    </article>
                  </FadeInOnScroll>
                );
              })}
            </div>
          ) : (
            <div className="landing-news-status">No news posts are currently featured.</div>
          )}

          <FadeInOnScroll delay={0.45}>
            <div className="landing-news-footer">
              <Link to="/news" className="cta-button">
                View All News
              </Link>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      <FullPageSection
        id="about"
        className="about-section"
        leftImage={sectionImages.about.left}
        rightImage={sectionImages.about.right}
        leftAlt="About Veloura"
        rightAlt="Veloura Creative"
        dataScrollSection
      >
        <div className="fullpage-container">
          <FadeInOnScroll>
            <h2 className="section-title">About Veloura</h2>
          </FadeInOnScroll>

          <FadeInOnScroll delay={0.2}>
            <div className="about-content">
              <p className="about-text">
                <strong>Veloura</strong> is a Los Santos based parent company built to bring creativity and professionalism under one name.
              </p>
              <p className="about-text">
                We aim to deliver unique experiences that blend artistry with quality, giving clients and collaborators a platform where their stories and style come alive.
              </p>
              <p className="about-text">
                Through our branches <strong>Veloura Studios®</strong> and <strong>Vague®</strong> we set trends and reimagine what creativity means in Los Santos.
              </p>
            </div>
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <a href="https://veloura.cv/team" className="cta-button">
                Meet the Team
              </a>
            </div>
          </FadeInOnScroll>
        </div>
      </FullPageSection>

      <FullPageSection
        id="studios"
        className="studios-section"
        leftImage={sectionImages.studios.left}
        rightImage={sectionImages.studios.right}
        leftAlt="Veloura Studios Photography"
        rightAlt="Professional Photography"
        dataScrollSection
      >
        <div className="fullpage-container">
          <FadeInOnScroll>
            <h2 className="section-title">Veloura Studios</h2>
          </FadeInOnScroll>

          <FadeInOnScroll delay={0.2}>
            <p className="section-intro">
              Our professional photography branch, the beginning of Veloura was founded here.
              Our photographers are dedicated to creating, capturing and presenting both people and companies alike, focusing on detail-driven, high quality photography for a reasonable price, with room to spend more to get more.
            </p>
          </FadeInOnScroll>

          <div className="services-grid">
            <FadeInOnScroll delay={0.3}>
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                    <path d="M2 8h20M9 2v6M15 2v6" />
                  </svg>
                </div>
                <div className="service-card-content">
                  <h4>Event Coverage</h4>
                  <p>From small gatherings to private celebrations.</p>
                </div>
              </div>
            </FadeInOnScroll>

            <FadeInOnScroll delay={0.4}>
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
                    <path d="M9 9v0M9 12v0M9 15v0M9 18v0" />
                  </svg>
                </div>
                <div className="service-card-content">
                  <h4>Business & Brand Shoots</h4>
                  <p>Visuals designed to make your company stand out.</p>
                </div>
              </div>
            </FadeInOnScroll>

            <FadeInOnScroll delay={0.5}>
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div className="service-card-content">
                  <h4>Personal Photography</h4>
                  <p>Portraits, duos, lifestyle, cars, and stylized shoots.</p>
                </div>
              </div>
            </FadeInOnScroll>

            <FadeInOnScroll delay={0.6}>
              <div className="service-card">
                <div className="service-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="8" />
                    <path d="M12 8v4l3 2" />
                    <path d="M8 12h8M12 8v8" />
                  </svg>
                </div>
                <div className="service-card-content">
                  <h4>Special Occasions</h4>
                  <p>Weddings, anniversaries, and milestones.</p>
                </div>
              </div>
            </FadeInOnScroll>
          </div>

          <FadeInOnScroll delay={0.7}>
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <Link to="/studios" className="cta-button">
                Visit
              </Link>
            </div>
          </FadeInOnScroll>
        </div>
      </FullPageSection>

      <FullPageSection
        id="vague"
        className="vague-section"
        leftImage={sectionImages.vague.left}
        rightImage={sectionImages.vague.right}
        leftAlt="Vague Fashion"
        rightAlt="Alt Streetwear"
        dataScrollSection
      >
        <div className="fullpage-container">
          <FadeInOnScroll>
            <h2 className="section-title">Vague</h2>
          </FadeInOnScroll>

          <FadeInOnScroll delay={0.2}>
            <p className="section-intro">
              Veloura's up and coming fashion branch, currently specializing in Alt focused Streetwear, with plans to expand into other cultures.
            </p>
          </FadeInOnScroll>

          <FadeInOnScroll delay={0.3}>
            <p className="section-text">
              Our goal with Vague is to deliver to, cater to, and to celebrate the many cultures brought about by fashion trends.
            </p>
          </FadeInOnScroll>

          <FadeInOnScroll delay={0.4}>
            <p className="section-text highlight">
              With limited drops and wearable art designed by our in-house creatives, you'll want to keep your eyes peeled.
            </p>
          </FadeInOnScroll>

          <FadeInOnScroll delay={0.5}>
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <Link to="/shop" className="cta-button">
                Shop
              </Link>
            </div>
          </FadeInOnScroll>
        </div>
      </FullPageSection>

      <Footer currentBrand="veloura" />
    </motion.div>
  );
};

export default VelouraLanding;
